import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import {
  ManagerPermission,
  ManagerRole,
  resolveAcl,
  can as resolveCan,
  canAccessScreen as resolveCanAccessScreen,
  canAccessScreenGroup as resolveCanAccessScreenGroup,
  hasAnyRole as resolveHasAnyRole,
  hasGlobalAccess as resolveHasGlobalAccess,
  isRole as resolveIsRole,
  resolvePrimaryRole,
} from '../../services/permissions/PermissionResolver';

// Re-exported for the many call sites that import these enums from this hook.
export { ManagerRole, ManagerPermission };

export default function usePermissions() {
  const { profile, company_acl, franchise_acl } = useSelector((state) => state.initialData);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const roles = profile?.roles ?? [];
  const permissions = profile?.permissions ?? [];

  const isSuperAdmin = profile?.is_super_admin === true;
  const isAdmin = isSuperAdmin || roles.includes(ManagerRole.Admin);
  const isSalesManager = isSuperAdmin || roles.includes(ManagerRole.SalesManager);
  const isOnboardingManager = isSuperAdmin || roles.includes(ManagerRole.OnboardingManager);
  const isOccAgent = isSuperAdmin || roles.includes(ManagerRole.OccAgent);
  const isMerchant = roles.includes(ManagerRole.StoreAdmin);

  const isMasterProvider = serviceProvider?.is_master === true;

  // Stable per profile/provider so the functions below (and every memo that
  // depends on them — e.g. useNavigation) don't get rebuilt on every render.
  const ctx = useMemo(
    () => ({ roles, permissions, isSuperAdmin, isMasterProvider }),
    [profile, serviceProvider?.is_master],
  );

  const hasGlobalAccess = useMemo(() => resolveHasGlobalAccess(company_acl), [company_acl]);

  const companyACL = useMemo(() => resolveAcl(company_acl, serviceProvider?.id), [company_acl, serviceProvider?.id]);

  const franchiseACL = useMemo(
    () => resolveAcl(franchise_acl, serviceProvider?.id),
    [franchise_acl, serviceProvider?.id],
  );

  const hasAnyRole = useCallback(
    (_roles, withSuperAdmin = true) => resolveHasAnyRole(ctx, _roles, withSuperAdmin),
    [ctx],
  );

  const isRole = useCallback((role) => resolveIsRole(ctx, role), [ctx]);

  const primaryRole = useMemo(() => resolvePrimaryRole(ctx), [ctx]);

  const can = useCallback((_permission) => resolveCan(ctx, _permission), [ctx]);

  const cant = useCallback((permission) => !can(permission), [can]);

  const canAccessScreenGroup = useCallback((screenGroup) => resolveCanAccessScreenGroup(ctx, screenGroup), [ctx]);

  const canAccessScreen = useCallback((screen) => resolveCanAccessScreen(ctx, screen), [ctx]);

  return {
    roles,
    primaryRole,
    isSuperAdmin,
    isAdmin,
    isOccAgent,
    isMerchant,
    isMasterProvider,
    isSalesManager,
    isOnboardingManager,
    hasGlobalAccess,
    companyACL,
    franchiseACL,
    can,
    cant,
    canAccessScreenGroup,
    canAccessScreen,
    hasAnyRole,
    isRole,
  };
}
